/// <reference path="./qz-tray.d.ts" />
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from './app-settings.service';
// Her app'in tsconfig.app.json'ı sadece kendi src/**/*.d.ts dosyalarını include ediyor,
// bu yüzden yukarıdaki triple-slash reference, ayrı duran qz-tray.d.ts'i derleme setine
// zorla dahil ediyor (bu paketin resmi tip tanımı da yok).
import * as qz from 'qz-tray';

/**
 * Artık varsayılan yazdırma yolu (tüm müşteriler geçti). `AppSettings.useJSPM` ile
 * açıkça istenmedikçe PrintFileService tarafından kullanılır (bkz. print-file.service.ts).
 *
 * Mesaj imzalama (setCertificatePromise/setSignaturePromise) aktif: sertifikanın public
 * kısmı `/assets/qz-certificate.txt`'ten okunuyor, her isteğin imzası backend'deki
 * Qz_SignMessage_Request servisinden (RSA-SHA512) alınıyor. Private key hiçbir zaman
 * tarayıcıya gelmiyor. Bu sayede QZ Tray, JSPM'deki "silent" davranışıyla aynı şekilde
 * diyalogsuz yazdırabiliyor.
 */
@Injectable({
  providedIn: 'root',
})
export class QzTrayPrintService {
  private connectPromise: Promise<void> | null = null;
  private securityConfigured = false;

  constructor(private http: HttpClient) {}

  private configureSecurity(): void {
    if (this.securityConfigured) {
      return;
    }
    this.securityConfigured = true;

    qz.security.setCertificatePromise((resolve: (v: string) => void, reject: (e: any) => void) => {
      fetch('/assets/qz-certificate.txt')
        .then((res) => (res.ok ? res.text() : Promise.reject('QZ sertifikası (qz-certificate.txt) yüklenemedi')))
        .then(resolve)
        .catch(reject);
    });

    qz.security.setSignatureAlgorithm('SHA512');

    qz.security.setSignaturePromise((toSign: string) => {
      return (resolve: (v: string) => void, reject: (e: any) => void) => {
        firstValueFrom(
          this.http.post<{ result: boolean; signature: string; errorMessage: string }>(
            `${environment.tmsService}api/Qz_SignMessage_Request`,
            { dataToSign: toSign }
          )
        )
          .then((res) => (res?.result ? resolve(res.signature) : reject(res?.errorMessage || 'İmzalama başarısız')))
          .catch(reject);
      };
    });
  }

  private connect(): Promise<void> {
    if (qz.websocket.isActive()) {
      return Promise.resolve();
    }
    this.configureSecurity();
    if (!this.connectPromise) {
      this.connectPromise = qz.websocket
        .connect()
        .catch((err: any) => {
          this.connectPromise = null;
          throw err;
        });
    }
    return this.connectPromise;
  }

  private getConnectErrorMessage(): string {
    return 'QZ Tray istemci uygulaması yüklü değil veya çalışmıyor! Lütfen QZ Tray\'i indirin ve çalıştırın: https://qz.io/download/';
  }

  /**
   * JSPrintManager'daki initializeJSPM() ile aynı rolü görür: ekran açılışında bağlantıyı
   * erkenden başlatır ki ilk "yazdır" tıklamasında bağlantı gecikmesi yaşanmasın.
   * JSPM'in start() metodu gibi bilerek non-blocking'tir; QZ Tray kurulu değilse burada
   * sessizce başarısız olur, gerçek hata ancak print() çağrıldığında kullanıcıya gösterilir.
   */
  initialize(): void {
    if (qz.websocket.isActive()) {
      return;
    }
    this.connect().catch(() => {
      // Kasıtlı olarak yutuldu: kullanıcı henüz yazdırma yapmadı, burada hata göstermek
      // gerekmiyor. print() çağrıldığında connect() zaten tekrar denenip hatayı raporlayacak.
    });
  }

  print(
    printerName: string,
    fileContent: any,
    printerType: 'W' | 'D',
    silent = true
  ): Promise<{ success: boolean; error: string }> {
    return this.connect()
      .catch(() => {
        throw new Error(this.getConnectErrorMessage());
      })
      .then(() => {
        const config = qz.configs.create(printerName);
        const data =
          printerType === 'W'
            ? [{ type: 'pixel', format: 'pdf', flavor: 'base64', data: fileContent }]
            : [{ type: 'raw', format: 'command', flavor: 'plain', data: fileContent }];

        return qz.print(config, data);
      })
      .then(() => ({ success: true, error: '' }))
      .catch((err: any) => ({
        success: false,
        error: err?.message || String(err) || 'QZ Tray baskı hatası',
      }));
  }

  getPrinters(): Promise<string[]> {
    return this.connect().then(() => qz.printers.find());
  }

  isConnected(): boolean {
    return qz.websocket.isActive();
  }
}
